/**
 * PURE computation of a video's RESUME point (F3-F). When the player mounts
 * the local `video_watch_progress` is read (position + completed) and it decides
 * which second to jump to, avoiding resuming right at the end or re-showing an
 * already-completed video from the last second.
 */

export interface ResumeInput {
  /** Saved position (seconds), or null if there's no progress. */
  positionSeconds: number | null | undefined;
  /** Video duration (seconds), 0/unknown if there's no metadata yet. */
  durationSeconds: number | null | undefined;
  /** Is the progress marked as completed? */
  completed?: boolean;
}

/** Margin (seconds) from the end within which resuming makes NO sense. */
const END_MARGIN_SECONDS = 5;
/** Margin (seconds) from the start below which it starts at 0. */
const START_MARGIN_SECONDS = 3;

/**
 * The second to jump to when opening the player, or `null` to start from the
 * beginning (no `seek`).
 *
 *  - No position or negligible position → `null` (starts at 0).
 *  - Completed video → `null` (re-watch from the start).
 *  - Position within the end margin → `null` (avoids resuming in the credits).
 *  - Otherwise → the saved position (clamped to the known duration).
 */
export function computeResumeSeek(input: ResumeInput): number | null {
  const pos = input.positionSeconds ?? 0;
  if (!Number.isFinite(pos) || pos <= START_MARGIN_SECONDS) {
    return null;
  }
  if (input.completed) {
    return null;
  }
  const duration = input.durationSeconds ?? 0;
  if (Number.isFinite(duration) && duration > 0) {
    if (pos >= duration - END_MARGIN_SECONDS) {
      return null;
    }
    return Math.min(pos, duration - END_MARGIN_SECONDS);
  }
  return pos;
}

/** Formats seconds to `m:ss` / `h:mm:ss` (player time labels). */
export function formatTime(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return "0:00";
  }
  const s = Math.floor(totalSeconds % 60);
  const m = Math.floor((totalSeconds / 60) % 60);
  const h = Math.floor(totalSeconds / 3600);
  const ss = s.toString().padStart(2, "0");
  if (h > 0) {
    const mm = m.toString().padStart(2, "0");
    return `${h}:${mm}:${ss}`;
  }
  return `${m}:${ss}`;
}
